import { create } from 'zustand';

/**
 * Pending-mutation queue.
 *
 * When the dashboard is offline, mutating handlers can `enqueue(...)` a
 * thunk plus a human-readable description instead of throwing. The store
 * subscribes to `window` `online` events and replays everything in FIFO
 * order, removing each item only after its thunk resolves successfully so
 * a mid-replay failure leaves the rest queued for the next attempt.
 *
 * This module is the infrastructure layer; tasks 13/14 (the asset upload
 * and publish flows) wire the actual `enqueue` calls. We register the
 * `online` listener here so basic offline → online replay works without
 * any feature code being present yet.
 *
 * Validates Requirement 25.2 (retry on connectivity restored) and 32.1
 * (graceful behavior under flaky network).
 */

export interface PendingMutation {
  /** Stable id, generated if not provided to `enqueue`. */
  id: string;
  /** Vietnamese short label rendered in any pending-queue UI. */
  description: string;
  /** Thunk re-run by `replayAll`; should resolve when the API call succeeds. */
  run(): Promise<void>;
}

export type PendingMutationInput = Omit<PendingMutation, 'id'> & { id?: string };

interface QueuedMutationsStore {
  queue: PendingMutation[];
  /** Whether a `replayAll` pass is currently in flight. */
  isReplaying: boolean;
  /** Add a mutation to the tail of the queue. */
  enqueue(mutation: PendingMutationInput): string;
  /** Remove a mutation by id (call manually if a thunk is no longer relevant). */
  remove(id: string): void;
  /** Drop the entire queue without running anything (e.g. on sign-out). */
  clear(): void;
  /**
   * Run every queued mutation in FIFO order. Successful runs are removed
   * from the queue; the first failure aborts the pass so the remainder can
   * be retried by the next `online` event or manual call.
   */
  replayAll(): Promise<void>;
}

let mutationCounter = 0;
function genMutationId(): string {
  mutationCounter += 1;
  return `m-${Date.now().toString(36)}-${mutationCounter}`;
}

export const useQueuedMutationsStore = create<QueuedMutationsStore>(
  (set, get) => ({
    queue: [],
    isReplaying: false,
    enqueue: (mutation) => {
      const id = mutation.id ?? genMutationId();
      set((state) => ({
        queue: [
          ...state.queue,
          { id, description: mutation.description, run: mutation.run },
        ],
      }));
      return id;
    },
    remove: (id) =>
      set((state) => ({ queue: state.queue.filter((m) => m.id !== id) })),
    clear: () => set({ queue: [] }),
    replayAll: async () => {
      if (get().isReplaying) {
        // Avoid overlapping passes if `online` fires twice in quick succession.
        return;
      }
      set({ isReplaying: true });
      try {
        // Snapshot the queue: items that fail will remain because we only
        // remove them on success. New items enqueued mid-replay get picked
        // up on the next pass.
        const snapshot = [...get().queue];
        for (const mutation of snapshot) {
          try {
            await mutation.run();
            set((state) => ({
              queue: state.queue.filter((m) => m.id !== mutation.id),
            }));
          } catch {
            // Stop on first failure so we don't paper over a real error.
            break;
          }
        }
      } finally {
        set({ isReplaying: false });
      }
    },
  }),
);

// Module-level side effect: when the browser goes back online, drain the
// queue. Guarded for SSR / non-browser test environments.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    void useQueuedMutationsStore.getState().replayAll();
  });
}
